import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";
import { AlertModalPasswordResetImage } from "../../../assets";
import { AppLayout } from "../../../containers/layout/app";
import { PasswordResetWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseButton } from "../../../components/button/styled";
import Cookies from "universal-cookie";
import { resetPasswordService } from "../../../utils/api/usermanagement/password/resetPassword";
import { BaseAlertModal } from "../../../components/modal/alert";

export const PasswordReset = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = async () => {
        await setIsAlertModalOpen(false);
        return navigate("/");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await resetPasswordService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to update password. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update password. ${error.message}`);
            console.error('Failed to update password:', error);
        }
    }

    return (
        <AppLayout
            pageId={"Password-Reset"}
            pageTitle={"Password Reset"}
        >
            <PasswordResetWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalPasswordResetImage />}
                    title="Password Reset!"
                    message="Password has been successfully reset"
                    callToAction="Continue"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--dark-color)"
                    >
                        Change Password
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Current Password
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="password"
                                    name="currentPassword"
                                    value={formDetails.currentPassword}
                                    placeholder="Current Password"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    New Password
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="password"
                                    name="newPassword"
                                    value={formDetails.newPassword}
                                    placeholder="New Password"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Confirm Password
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="password"
                                    name="confirmPassword"
                                    value={formDetails.confirmPassword}
                                    placeholder="Confirm Password"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update Password
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </PasswordResetWrapper>
        </AppLayout>
    )
}